import { useState, useEffect, useMemo } from 'react';
import { cxcApi, parametrosApi } from '../../utils/api';
import {
  Users, Search, FileText, AlertCircle, CheckCircle2,
  DollarSign, Banknote, Clock, ChevronRight, X, Calendar,
  CreditCard, Building2, Check, Eye, Download, FileSpreadsheet,
  Mail, Send, Loader2, Settings, Save, Wallet
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import useAuthStore from '../../store/authStore';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import { ChipSaldoFavor, BloqueExcedente, RegistrarSaldoFavorModal, SaldoFavorModal } from '../../components/cxc/SaldoFavor';

const TIPO_DOC = {
  '01': 'FE',
  '02': 'ND',
  '03': 'NC',
  '04': 'TE',
  '08': 'FEC',
  '09': 'FEE'
};

export default function PendientesPorCliente() {
  const { currentEmisor } = useAuthStore();
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCliente, setSearchCliente] = useState('');
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const [bancos, setBancos] = useState([]);
  // Hook responsive: empuja los botones Excel/PDF/Estado de Cuenta a la derecha si el
  // nombre del cliente (absolute) los traslaparía.
  const { searchRef: clientNameRef, buttonsRef: clientButtonsRef } = useToolbarResponsive([selectedClienteId]);

  // Modal de pago
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);

  // Pago múltiple
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [showPagoMultipleModal, setShowPagoMultipleModal] = useState(false);

  // Modal de vista previa
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFacturaId, setPreviewFacturaId] = useState(null);

  // Modal de Estado de Cuenta (envío por email)
  const [showEstadoCuentaModal, setShowEstadoCuentaModal] = useState(false);

  const [pagoData, setPagoData] = useState({
    monto: '',
    fechaPago: dayjs().format('YYYY-MM-DD'),
    medioPago: 'transferencia',
    numeroComprobante: '',
    banco: '',
    notas: ''
  });
  const [pagoFile, setPagoFile] = useState(null);
  const [aplicandoPago, setAplicandoPago] = useState(false);
  // Cobrar en colones una factura en USD (manejo de diferencial cambiario)
  const [cobrarEnColones, setCobrarEnColones] = useState(false);
  const [montoCRC, setMontoCRC] = useState('');
  // Retención 2% de renta (cliente entidad del Estado)
  const [aplicarRetencion, setAplicarRetencion] = useState(false);
  // Saldo a favor: disponible por cliente {receptorId: {CRC, USD}}, excedente del cobro y modales
  const [saldosFavor, setSaldosFavor] = useState({});
  const [excedenteSF, setExcedenteSF] = useState(null);
  const [showRegistrarSF, setShowRegistrarSF] = useState(false);
  const [vistaSaldoFavor, setVistaSaldoFavor] = useState(null);

  useEffect(() => {
    loadFacturas();
    loadBancos();
  }, []);
  useEffect(() => { if (!showPagoModal) setExcedenteSF(null); }, [showPagoModal]);

  const loadBancos = async () => {
    try {
      const response = await parametrosApi.listBancosActivos();
      setBancos(response.data.data || []);
    } catch (error) {
      console.error('Error loading bancos:', error);
    }
  };

  const loadSaldosFavor = async () => {
    try { setSaldosFavor((await cxcApi.saldoFavorResumen()).data.data || {}); }
    catch (e) { console.error('Error loading saldos a favor:', e); }
  };

  const loadFacturas = async () => {
    loadSaldosFavor();
    try {
      setLoading(true);
      // Cargar todas las facturas pendientes y parciales
      const response = await cxcApi.listFacturas({ estado: 'pendiente', limit: 500 });
      const pendientes = response.data.data || [];

      // También cargar las parciales
      const responseParcial = await cxcApi.listFacturas({ estado: 'parcial', limit: 500 });
      const parciales = responseParcial.data.data || [];

      setFacturas([...pendientes, ...parciales]);
    } catch (error) {
      console.error('Error loading facturas:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar facturas por cliente
  const clientesConFacturas = useMemo(() => {
    const clienteMap = new Map();

    facturas.forEach(f => {
      const clienteKey = f.receptorId || f.receptorIdentificacion || 'sin-cliente';

      if (!clienteMap.has(clienteKey)) {
        clienteMap.set(clienteKey, {
          id: clienteKey,
          nombre: f.receptorNombre || 'Cliente sin nombre',
          identificacion: f.receptorIdentificacion || '',
          email: f.receptorEmail || '',
          facturas: [],
          totalCRC: 0,
          totalUSD: 0,
          cantidadCRC: 0,
          cantidadUSD: 0,
          vencidas: 0,
          tieneVencidas: false
        });
      }

      const cliente = clienteMap.get(clienteKey);
      cliente.facturas.push(f);

      const saldo = parseFloat(f.saldoPendiente) || 0;
      if (f.moneda === 'USD') {
        cliente.totalUSD += saldo;
        cliente.cantidadUSD++;
      } else {
        cliente.totalCRC += saldo;
        cliente.cantidadCRC++;
      }

      // Verificar si está vencida
      if (f.fechaVencimiento && dayjs(f.fechaVencimiento).isBefore(dayjs(), 'day')) {
        cliente.vencidas++;
        cliente.tieneVencidas = true;
      }
    });

    // Convertir a array y ordenar por nombre alfabéticamente
    return Array.from(clienteMap.values())
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [facturas]);

  // Filtrar clientes por búsqueda (nombre, cédula o número de FE/consecutivo).
  // Como se cargan TODAS las pendientes, buscar por consecutivo encuentra al cliente
  // que tiene esa FE aunque sea de cualquier mes.
  const clientesFiltrados = useMemo(() => {
    if (!searchCliente.trim()) return clientesConFacturas;
    const search = searchCliente.toLowerCase();
    return clientesConFacturas.filter(c =>
      c.nombre.toLowerCase().includes(search) ||
      c.identificacion.toLowerCase().includes(search) ||
      c.facturas.some(f =>
        (f.consecutivo || '').toLowerCase().includes(search) ||
        (f.clave || '').toLowerCase().includes(search)
      )
    );
  }, [clientesConFacturas, searchCliente]);

  // Cliente seleccionado
  const clienteSeleccionado = useMemo(() => {
    if (!selectedClienteId) return null;
    return clientesConFacturas.find(c => c.id === selectedClienteId);
  }, [clientesConFacturas, selectedClienteId]);

  // Seleccionar primer cliente por defecto
  useEffect(() => {
    if (clientesFiltrados.length > 0 && !selectedClienteId) {
      setSelectedClienteId(clientesFiltrados[0].id);
    }
  }, [clientesFiltrados, selectedClienteId]);

  // Reset selected facturas when client changes
  useEffect(() => {
    setSelectedFacturas([]);
  }, [selectedClienteId]);

  // Toggle factura selection
  const toggleFacturaSelection = (factura) => {
    setSelectedFacturas(prev => {
      const isSelected = prev.some(f => f.id === factura.id);
      if (isSelected) {
        return prev.filter(f => f.id !== factura.id);
      } else {
        // Only allow same currency selection
        if (prev.length > 0 && prev[0].moneda !== factura.moneda) {
          toast.error('Solo puede seleccionar facturas de la misma moneda');
          return prev;
        }
        return [...prev, factura];
      }
    });
  };

  // Select all facturas of same currency
  const toggleSelectAll = (moneda) => {
    if (!clienteSeleccionado) return;
    const facturasMoneda = clienteSeleccionado.facturas.filter(f => f.moneda === moneda);
    const allSelected = facturasMoneda.every(f => selectedFacturas.some(sf => sf.id === f.id));

    if (allSelected) {
      setSelectedFacturas(prev => prev.filter(f => f.moneda !== moneda));
    } else {
      setSelectedFacturas(facturasMoneda);
    }
  };

  // Calculate total selected
  const totalSeleccionado = useMemo(() => {
    return selectedFacturas.reduce((sum, f) => sum + (parseFloat(f.saldoPendiente) || 0), 0);
  }, [selectedFacturas]);

  // Formato con separadores de miles para inputs numéricos. El estado guarda el valor
  // crudo (ej. "1203663.67") y se muestra formateado (ej. "1,203,663.67").
  const formatNumInput = (raw) => {
    if (raw === '' || raw == null) return '';
    const [intPart, decPart] = String(raw).split('.');
    const intFmt = (intPart || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${intFmt}.${decPart}` : intFmt;
  };
  const parseNumInput = (formatted) => {
    let clean = String(formatted).replace(/[^\d.]/g, '');
    const i = clean.indexOf('.');
    if (i !== -1) clean = clean.slice(0, i + 1) + clean.slice(i + 1).replace(/\./g, '');
    return clean;
  };

  const formatMoney = (amount, moneda = 'CRC') => {
    const num = parseFloat(amount) || 0;
    if (moneda === 'USD') {
      return `$ ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡ ${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('DD/MM/YYYY');
  };

  const getDiasVencido = (fechaVencimiento) => {
    if (!fechaVencimiento) return null;
    const dias = dayjs().diff(dayjs(fechaVencimiento), 'day');
    return dias;
  };

  const openPreviewModal = (factura) => {
    setPreviewFacturaId(factura.id);
    setShowPreviewModal(true);
  };

  const openPagoModal = (factura) => {
    setSelectedFactura(factura);
    setPagoData({
      monto: factura.saldoPendiente?.toFixed(2) || '',
      fechaPago: dayjs().format('YYYY-MM-DD'),
      medioPago: 'transferencia',
      numeroComprobante: '',
      banco: '',
      notas: ''
    });
    setPagoFile(null);
    setCobrarEnColones(false);
    setMontoCRC('');
    setAplicarRetencion(false);
    setShowPagoModal(true);
  };

  const handleAplicarPago = async (e) => {
    if (e) e.preventDefault();
    if (!selectedFactura) return;

    const monto = parseFloat(pagoData.monto);
    if (isNaN(monto) || monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }

    if (monto > selectedFactura.saldoPendiente + 0.01) {
      toast.error('El monto excede el saldo pendiente');
      return;
    }

    const esCobroEnColones = cobrarEnColones && selectedFactura.moneda === 'USD';
    if (esCobroEnColones && (!montoCRC || parseFloat(montoCRC) <= 0)) {
      toast.error('Ingrese el monto real recibido en colones');
      return;
    }

    try {
      setAplicandoPago(true);
      const data = {
        monto,
        fechaPago: pagoData.fechaPago,
        medioPago: pagoData.medioPago,
        numeroComprobante: pagoData.numeroComprobante || null,
        banco: pagoData.banco || null,
        notas: pagoData.notas || null
      };
      if (esCobroEnColones && montoCRC) {
        data.pagarEnColones = 'true';
        data.montoCRC = montoCRC;
      }
      if (aplicarRetencion) {
        data.aplicarRetencion = 'true';
      }
      if (excedenteSF) Object.assign(data, excedenteSF);
      const resp = await cxcApi.aplicarPago(selectedFactura.id, data, pagoFile);
      const sf = resp?.data?.data?.saldoFavor;
      if (sf?.error) toast.error(`Cobro aplicado, pero no se registró el saldo a favor: ${sf.error}`, { duration: 8000 });
      else if (sf) toast.success(`Saldo a favor registrado: ${formatMoney(sf.monto, sf.moneda)}`);

      toast.success('Pago aplicado exitosamente');
      if (resp?.data?.warning) {
        toast(resp.data.warning, { icon: '⚠️', duration: 8000, style: { background: '#fef3c7', color: '#92400e' } });
      }
      setShowPagoModal(false);
      setCobrarEnColones(false);
      setMontoCRC('');
      setAplicarRetencion(false);
      // Limpiar selección — si no, la barra "Cobro individual: XXX" queda activa
      // y al seleccionar otra FE parece que se acumula con la anterior.
      setSelectedFacturas([]);
      loadFacturas();
    } catch (error) {
      console.error('Error aplicando pago:', error);
      toast.error(error.response?.data?.error || 'Error al aplicar cobro');
    } finally {
      setAplicandoPago(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Layout Master-Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[var(--alto-contenido)]">

        {/* Panel Izquierdo - Lista de Clientes */}
        <div className="lg:col-span-1 bg-white rounded-xl border flex flex-col overflow-hidden">
          {/* Búsqueda */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar cliente o N° de FE..."
                value={searchCliente}
                onChange={(e) => setSearchCliente(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Lista de Clientes */}
          <div className="flex-1 overflow-y-auto">
            {clientesFiltrados.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p>No hay clientes con facturas pendientes</p>
              </div>
            ) : (
              <div className="divide-y">
                {clientesFiltrados.map((cliente) => (
                  <button
                    key={cliente.id}
                    onClick={() => setSelectedClienteId(cliente.id)}
                    className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                      selectedClienteId === cliente.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{cliente.nombre}</p>
                        <p className="text-xs text-gray-500">{cliente.identificacion}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {cliente.tieneVencidas && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                            {cliente.vencidas} venc.
                          </span>
                        )}
                        <ChipSaldoFavor saldo={saldosFavor[cliente.id]} />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer con conteo */}
          <div className="px-3 py-2 border-t bg-gray-50 text-xs text-gray-500 text-center font-medium">
            {clientesFiltrados.length} cliente{clientesFiltrados.length !== 1 ? 's' : ''}
          </div>

        </div>

        {/* Panel Derecho - Detalle del Cliente */}
        <div className="lg:col-span-2 bg-white rounded-xl border flex flex-col overflow-hidden">
          {!clienteSeleccionado ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Users className="h-16 w-16 mx-auto text-gray-300 mb-3" />
                <p className="font-medium">Seleccione un cliente</p>
                <p className="text-sm">para ver sus facturas pendientes</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header del cliente — responsive: si el nombre es largo, los botones se desplazan a la derecha */}
              <div className="p-4 border-b bg-white relative">
                <div ref={clientNameRef} className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3 z-10">
                  <div className="p-3 bg-gray-100 rounded-xl flex-shrink-0">
                    <Building2 className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900" title={clienteSeleccionado.nombre}>{clienteSeleccionado.nombre}</h2>
                    <p className="text-sm text-gray-500">{clienteSeleccionado.identificacion}</p>
                  </div>
                </div>
                <div ref={clientButtonsRef} className="flex items-center justify-center gap-2 py-2">
                    <button
                      onClick={async () => {
                        try {
                          const res = await cxcApi.exportFacturasExcel(`receptorId=${clienteSeleccionado.id}`);
                          const url = URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a'); a.href = url;
                          a.download = `CxC_${clienteSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
                          a.click(); URL.revokeObjectURL(url);
                          toast.success('Excel exportado');
                        } catch (e) { toast.error('Error al exportar Excel'); }
                      }}
                      className="flex flex-col items-center px-3 py-1.5 rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50 text-[10px] font-medium"
                    >
                      <FileSpreadsheet size={16} />
                      Excel
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await cxcApi.exportFacturasPdf(`receptorId=${clienteSeleccionado.id}`);
                          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          const a = document.createElement('a'); a.href = url;
                          a.download = `CxC_${clienteSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
                          a.click(); URL.revokeObjectURL(url);
                          toast.success('PDF exportado');
                        } catch (e) { toast.error('Error al exportar PDF'); }
                      }}
                      className="flex flex-col items-center px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-[10px] font-medium"
                    >
                      <Download size={16} />
                      PDF
                    </button>
                    <button
                      onClick={() => setShowEstadoCuentaModal(true)}
                      className="flex flex-col items-center px-3 py-1.5 rounded-lg border border-violet-300 text-violet-600 hover:bg-violet-50 text-[10px] font-medium"
                    >
                      <Mail size={16} />
                      Estado de Cuenta
                    </button>
                    <button
                      onClick={() => setShowRegistrarSF(true)}
                      className="flex flex-col items-center px-3 py-1.5 rounded-lg border border-emerald-400 text-emerald-700 hover:bg-emerald-50 text-[10px] font-medium"
                    >
                      <Wallet size={16} />
                      Saldo a favor
                    </button>
                  </div>
              </div>

              {/* Cards de Totales */}
              <div className="p-4 border-b">
                <div className={`grid grid-cols-2 ${saldosFavor[clienteSeleccionado.id] ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`}>
                  {/* CRC */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Banknote className="h-4 w-4 text-green-500" />
                      <span className="text-xs font-medium text-gray-700">Colones</span>
                    </div>
                    <p className="text-lg font-bold text-green-600 ">
                      {formatMoney(clienteSeleccionado.totalCRC, 'CRC')}
                    </p>
                    <p className="text-xs text-gray-500">{clienteSeleccionado.cantidadCRC} fact.</p>
                  </div>

                  {/* USD */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-4 w-4 text-blue-500" />
                      <span className="text-xs font-medium text-gray-700">Dólares</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600 ">
                      {formatMoney(clienteSeleccionado.totalUSD, 'USD')}
                    </p>
                    <p className="text-xs text-gray-500">{clienteSeleccionado.cantidadUSD} fact.</p>
                  </div>

                  {/* Vencidas */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className={`h-4 w-4 ${clienteSeleccionado.vencidas > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                      <span className="text-xs font-medium text-gray-700">Vencidas</span>
                    </div>
                    <p className={`text-lg font-bold  ${clienteSeleccionado.vencidas > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {clienteSeleccionado.vencidas}
                    </p>
                    <p className={`text-xs ${clienteSeleccionado.vencidas > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                      {clienteSeleccionado.vencidas > 0 ? 'Requiere atención' : 'Al día'}
                    </p>
                  </div>

                  {/* Total Facturas */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-purple-500" />
                      <span className="text-xs font-medium text-gray-700">Total</span>
                    </div>
                    <p className="text-lg font-bold text-purple-600 ">
                      {clienteSeleccionado.facturas.length}
                    </p>
                    <p className="text-xs text-gray-500">Facturas pend.</p>
                  </div>
                  {saldosFavor[clienteSeleccionado.id] && (
                    <button onClick={() => setVistaSaldoFavor('historial')} className="text-left bg-white rounded-lg border border-emerald-500 p-3 hover:bg-emerald-50/40">
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet className="h-4 w-4 text-emerald-600" />
                        <span className="text-xs font-medium text-emerald-700">Saldo a favor</span>
                      </div>
                      {Object.entries(saldosFavor[clienteSeleccionado.id]).map(([m, v]) => (
                        <p key={m} className="text-lg font-bold text-emerald-700">{formatMoney(v, m)}</p>
                      ))}
                      <span onClick={(e) => { e.stopPropagation(); setVistaSaldoFavor('aplicar'); }} className="text-xs text-emerald-700 underline underline-offset-2">Aplicar a facturas →</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Barra de Cobro */}
              {selectedFacturas.length > 0 && (
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedFacturas.length === 1 ? (
                      <span className="text-sm font-medium text-blue-800">
                        Cobro individual: <strong>{selectedFacturas[0].consecutivo}</strong>
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-blue-800">
                        Cobro Múltiple de {selectedFacturas.length} facturas para <strong>{clienteSeleccionado?.nombre}</strong>
                      </span>
                    )}
                    <span className="text-sm text-blue-600">
                      Total: <strong>{formatMoney(totalSeleccionado, selectedFacturas[0]?.moneda)}</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedFacturas([])}
                      className="px-4 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => {
                        if (selectedFacturas.length === 1) {
                          openPagoModal(selectedFacturas[0]);
                        } else {
                          setShowPagoMultipleModal(true);
                        }
                      }}
                      className="px-4 py-1.5 text-sm font-medium border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 flex items-center gap-2"
                    >
                      <CreditCard className="h-4 w-4" />
                      Aplicar Cobro
                    </button>
                  </div>
                </div>
              )}

              {/* Tabla de Facturas */}
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-center w-10">
                        <input
                          type="checkbox"
                          checked={clienteSeleccionado?.facturas.length > 0 &&
                            clienteSeleccionado.facturas.every(f => selectedFacturas.some(sf => sf.id === f.id))}
                          onChange={() => {
                            if (clienteSeleccionado?.facturas.length > 0) {
                              const moneda = clienteSeleccionado.facturas[0]?.moneda || 'CRC';
                              toggleSelectAll(moneda);
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tipo Doc.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Factura</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Emisión</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Vencimiento</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Cobrado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {clienteSeleccionado.facturas
                      .sort((a, b) => new Date(a.fechaVencimiento) - new Date(b.fechaVencimiento))
                      .map((factura) => {
                        const diasVencido = getDiasVencido(factura.fechaVencimiento);
                        const estaVencida = diasVencido !== null && diasVencido > 0;

                        const isSelected = selectedFacturas.some(sf => sf.id === factura.id);

                        return (
                          <tr key={factura.id} className={`hover:bg-gray-50 ${estaVencida ? 'bg-red-50/50' : ''} ${isSelected ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleFacturaSelection(factura)}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {TIPO_DOC[factura.tipoDocumento] || factura.tipoDocumento || 'FE'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openPreviewModal(factura)}
                                  className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                  title="Ver factura"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <span className="font-medium text-gray-900">{factura.consecutivo}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatDate(factura.fechaEmision)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatDate(factura.fechaVencimiento)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {(() => {
                                // Si tiene cobro parcial, mostrar ese estado primero
                                if ((factura.totalPagado || 0) > 0) {
                                  return (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                                      Cobro Parcial
                                    </span>
                                  );
                                }

                                const dias = diasVencido !== null ? -diasVencido : null;
                                if (dias === null) return <span className="text-gray-400 text-xs">-</span>;
                                if (dias > 10) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-green-300 text-green-700 rounded">Vence {dias}d</span>;
                                } else if (dias > 1) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-yellow-300 text-yellow-700 rounded">Vence {dias}d</span>;
                                } else if (dias === 1) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-orange-300 text-orange-700 rounded">Mañana</span>;
                                } else if (dias === 0) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium border border-red-300 text-red-700 rounded">Hoy</span>;
                                } else {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium border border-red-400 text-red-700 rounded">Venció {Math.abs(dias)}d</span>;
                                }
                              })()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {formatMoney(factura.totalComprobante, factura.moneda)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {(factura.totalPagado || 0) > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                  {formatMoney(factura.totalPagado, factura.moneda)}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`text-sm font-bold ${
                                factura.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'
                              }`}>
                                {formatMoney(factura.saldoPendiente, factura.moneda)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de Pago - Estilo bonito */}
      {showPagoModal && selectedFactura && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b-[3px] border-b-green-500 bg-white rounded-t-xl sticky top-0 z-10">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl border border-green-400 flex items-center justify-center"><CreditCard className="h-4 w-4 text-green-600" /></span>
                Aplicar Pago
              </h3>
              <button onClick={() => setShowPagoModal(false)} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                <X size={20} />
              </button>
            </div>

            {/* Invoice Summary */}
            <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-900">{selectedFactura.receptorNombre}</p>
                  <p className="text-sm text-gray-500 font-mono mt-0.5">{selectedFactura.consecutivo}</p>
                </div>
                <div className="sm:text-right">
                  <p className="text-xs text-gray-500">Total Pendiente</p>
                  <p className="text-2xl font-bold text-green-700 ">
                    {formatMoney(selectedFactura.saldoPendiente, selectedFactura.moneda)}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <div>
                  <span className="text-gray-500">Total factura:</span>
                  <span className="ml-2 text-sm font-medium">{formatMoney(selectedFactura.totalComprobante, selectedFactura.moneda)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Ya pagado:</span>
                  <span className="ml-2 text-sm font-medium">{formatMoney(selectedFactura.totalPagado || 0, selectedFactura.moneda)}</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleAplicarPago} className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
                {/* Columna izquierda: datos del pago */}
                <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Pago *
                  </label>
                  <input
                    type="date"
                    value={pagoData.fechaPago}
                    onChange={(e) => setPagoData({ ...pagoData, fechaPago: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monto a Aplicar *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={pagoData.monto}
                    onChange={(e) => setPagoData({ ...pagoData, monto: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  <div className="flex gap-1 mt-1">
                    {[25, 50, 75, 100].map(pct => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setPagoData({ ...pagoData, monto: ((selectedFactura.saldoPendiente || 0) * pct / 100).toFixed(2) })}
                        className="flex-1 text-xs py-1 border border-gray-300 rounded hover:bg-green-50 hover:border-green-400 hover:text-green-700 transition-colors font-medium text-gray-600"
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Saldo Restante
                  </label>
                  {(() => {
                    const saldoRestante = Math.max(0, (selectedFactura.saldoPendiente || 0) - (parseFloat(pagoData.monto) || 0));
                    return (
                      <div className={`w-full border rounded-lg px-3 py-2  font-semibold ${
                        saldoRestante <= 0.01
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                      }`}>
                        {formatMoney(saldoRestante, selectedFactura.moneda)}
                      </div>
                    );
                  })()}
                </div>
                </div>
                {/* Columna derecha: cobro en colones / retención / resumen */}
                <div className="space-y-4">
              {/* Saldo a favor: el depósito trajo de más (en la moneda en que se recibió) */}
              <BloqueExcedente
                moneda={cobrarEnColones && selectedFactura.moneda === 'USD' ? 'CRC' : (selectedFactura.moneda || 'CRC')}
                cobrado={cobrarEnColones && selectedFactura.moneda === 'USD' ? (parseFloat(montoCRC) || 0) : (parseFloat(pagoData.monto) || 0)}
                onChange={setExcedenteSF}
              />
              {/* Cobrar en Colones (solo para facturas USD) */}
              {selectedFactura.moneda === 'USD' && (
                <div className="p-3 border border-amber-200 rounded-lg bg-amber-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cobrarEnColones}
                      onChange={(e) => { setCobrarEnColones(e.target.checked); if (!e.target.checked) setMontoCRC(''); }}
                      className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm font-medium text-amber-800">Cobrar en Colones (₡)</span>
                  </label>
                  {cobrarEnColones && (() => {
                    // T/C efectivo desde el monto en colones GUARDADO al emitir la FE
                    // (base+IVA+otros en ₡ ÷ total USD). No recalcula: respeta el redondeo de Hacienda.
                    const tcFactura = (selectedFactura.totalColones > 0 && selectedFactura.totalComprobante > 0)
                      ? (selectedFactura.totalColones / selectedFactura.totalComprobante)
                      : (selectedFactura.tipoCambio || 1);
                    const deudaCRC = (parseFloat(pagoData.monto) || 0) * tcFactura;
                    return (
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Total factura en ₡ (T/C emisión)</label>
                          <div className="px-3 py-2 bg-white border rounded-lg text-sm font-mono text-gray-700">
                            ₡{deudaCRC.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <p className="text-[10px] text-gray-500 mt-0.5">T/C factura: ₡{tcFactura.toFixed(2)}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Monto recibido en ₡ *</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formatNumInput(montoCRC)}
                            onChange={(e) => setMontoCRC(parseNumInput(e.target.value))}
                            placeholder="Monto real en colones"
                            className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            required={cobrarEnColones}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Retención 2% de renta (cliente entidad del Estado) */}
              <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aplicarRetencion}
                    onChange={(e) => setAplicarRetencion(e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-purple-800">Retención 2% renta (entidad del Estado)</span>
                </label>
                {aplicarRetencion && !cobrarEnColones && (() => {
                  const fmt = (n) => (n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  const simb = selectedFactura.moneda === 'USD' ? '$' : '₡';
                  const montoAplicar = parseFloat(pagoData.monto) || 0;
                  const total = selectedFactura.totalComprobante || 0;
                  const subtotal = selectedFactura.subtotal || 0;
                  // Base proporcional al monto aplicado (soporta cobros parciales)
                  const baseRet = total > 0 ? subtotal * (montoAplicar / total) : subtotal;
                  const retencion = Math.round(baseRet * 0.02 * 100) / 100;
                  const neto = montoAplicar - retencion;
                  return (
                    <div className="mt-3 text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base (subtotal sin IVA):</span>
                        <span className="font-mono">{simb}{fmt(baseRet)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Retención 2%:</span>
                        <span className="font-mono font-medium text-purple-700">−{simb}{fmt(retencion)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-1">
                        <span className="font-medium text-gray-700">Neto a recibir:</span>
                        <span className="font-mono font-bold text-gray-900">{simb}{fmt(neto)}</span>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">La factura se salda al 100%; la retención es crédito fiscal de renta (no es pendiente).</p>
                    </div>
                  );
                })()}
              </div>

              {/* Resumen del pago en colones: la deuda total en ₡ se reparte entre
                  retención 2% + colones recibidos + diferencia cambiaria → cuadra a ₡0.
                  La retención NO es pérdida cambiaria; se muestran por separado. */}
              {cobrarEnColones && montoCRC && (() => {
                const fmt = (n) => (n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const montoUSD = parseFloat(pagoData.monto) || 0;
                const tcFactura = (selectedFactura.totalColones > 0 && selectedFactura.totalComprobante > 0)
                  ? (selectedFactura.totalColones / selectedFactura.totalComprobante)
                  : (selectedFactura.tipoCambio || 1);
                const total = selectedFactura.totalComprobante || 0;
                const subtotal = selectedFactura.subtotal || 0;
                const retencionUSD = aplicarRetencion ? Math.round((total > 0 ? subtotal * (montoUSD / total) : subtotal) * 0.02 * 100) / 100 : 0;
                const deudaTotalCRC = Math.round(montoUSD * tcFactura * 100) / 100;
                const retencionCRC = Math.round(retencionUSD * tcFactura * 100) / 100;
                const recibidoCRC = parseFloat(montoCRC) || 0;
                const residual = Math.round((deudaTotalCRC - retencionCRC - recibidoCRC) * 100) / 100; // >0 pérdida, <0 ganancia
                const esPerdida = residual >= 0;
                return (
                  <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                    <p className="text-sm font-semibold text-blue-900 mb-2">Resumen del pago (en ₡)</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Total factura (T/C emisión)</span>
                        <span className="font-mono">₡{fmt(deudaTotalCRC)}</span>
                      </div>
                      {aplicarRetencion && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">(−) Retención 2% renta (crédito fiscal)</span>
                          <span className="font-mono text-purple-700">−₡{fmt(retencionCRC)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">(−) Recibido en colones</span>
                        <span className="font-mono">−₡{fmt(recibidoCRC)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={esPerdida ? 'text-red-700' : 'text-green-700'}>
                          ({esPerdida ? '−' : '+'}) {esPerdida ? 'Pérdida' : 'Ganancia'} por diferencial cambiario
                        </span>
                        <span className={`font-mono ${esPerdida ? 'text-red-700' : 'text-green-700'}`}>
                          {esPerdida ? '−' : '+'}₡{fmt(Math.abs(residual))}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-1 font-semibold text-gray-900">
                        <span>Diferencia (cuadra a ₡0)</span>
                        <span className="font-mono">₡{fmt(deudaTotalCRC - retencionCRC - recibidoCRC - residual)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medio de Pago
                  </label>
                  <select
                    value={pagoData.medioPago}
                    onChange={(e) => setPagoData({ ...pagoData, medioPago: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="sinpe">SINPE Movil</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. Comprobante
                  </label>
                  <input
                    type="text"
                    value={pagoData.numeroComprobante}
                    onChange={(e) => setPagoData({ ...pagoData, numeroComprobante: e.target.value })}
                    placeholder="Referencia"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banco
                  </label>
                  <select
                    value={pagoData.banco}
                    onChange={(e) => setPagoData({ ...pagoData, banco: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Seleccionar banco</option>
                    {bancos.map(banco => (
                      <option key={banco.id} value={banco.nombre}>
                        {banco.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comprobante (opcional)
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setPagoFile(e.target.files?.[0] || null)}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 pb-1 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => setShowPagoModal(false)}
                  className="px-5 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={aplicandoPago}
                  className="px-6 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 font-medium flex items-center gap-2"
                >
                  {aplicandoPago ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></span>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Aplicar Pago
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Vista Previa */}
      {showPreviewModal && previewFacturaId && (
        <PrintPreviewModal
          documentId={previewFacturaId}
          emisor={currentEmisor}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewFacturaId(null);
          }}
        />
      )}

      {/* Modal de Pago Múltiple */}
      {showPagoMultipleModal && selectedFacturas.length > 0 && (
        <PagoMultipleModal
          facturas={selectedFacturas}
          cliente={clienteSeleccionado}
          bancos={bancos}
          formatMoney={formatMoney}
          onClose={() => setShowPagoMultipleModal(false)}
          onSuccess={() => {
            setShowPagoMultipleModal(false);
            setSelectedFacturas([]);
            loadFacturas();
          }}
        />
      )}

      {/* Saldo a favor */}
      {showRegistrarSF && clienteSeleccionado && (
        <RegistrarSaldoFavorModal cliente={clienteSeleccionado} bancos={bancos}
          onClose={() => setShowRegistrarSF(false)}
          onSuccess={() => { setShowRegistrarSF(false); loadSaldosFavor(); }} />
      )}
      {vistaSaldoFavor && clienteSeleccionado && (
        <SaldoFavorModal cliente={clienteSeleccionado} facturas={clienteSeleccionado.facturas} bancos={bancos} vistaInicial={vistaSaldoFavor}
          onClose={() => setVistaSaldoFavor(null)}
          onSuccess={() => { setVistaSaldoFavor(null); loadFacturas(); }} />
      )}

      {/* Modal Estado de Cuenta (email) */}
      {showEstadoCuentaModal && clienteSeleccionado && (
        <EnviarEstadoCuentaModal
          cliente={clienteSeleccionado}
          saldoFavor={saldosFavor[clienteSeleccionado.id] || null}
          emisor={currentEmisor}
          onClose={() => setShowEstadoCuentaModal(false)}
        />
      )}
    </div>
  );
}

// Modal de Pago Múltiple
function PagoMultipleModal({ facturas, cliente, bancos, formatMoney, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [excedenteSF, setExcedenteSF] = useState(null); // excedente del depósito que queda como saldo a favor
  const [aplicarRetencion, setAplicarRetencion] = useState(false);
  // Cobro en colones de facturas USD (múltiple): backend prorratea diferencial por factura.
  const [cobrarEnColones, setCobrarEnColones] = useState(false);
  const [montoCRC, setMontoCRC] = useState('');
  const [formData, setFormData] = useState({
    fechaPago: dayjs().format('YYYY-MM-DD'),
    montoTotal: facturas.reduce((sum, f) => sum + (parseFloat(f.saldoPendiente) || 0), 0).toFixed(2),
    medioPago: 'transferencia',
    numeroComprobante: '',
    banco: '',
    notas: ''
  });

  const moneda = facturas[0]?.moneda || 'CRC';
  const totalPendiente = facturas.reduce((sum, f) => sum + (parseFloat(f.saldoPendiente) || 0), 0);

  // Calcular retención total: 2% del subtotal (sin IVA) prorrateado al monto aplicado por factura.
  // Aplica FIFO igual que el backend. Devuelve {usd, crc} — el CRC usa el T/C efectivo de
  // cada FE para mostrar la retención en la moneda que el cliente realmente descuenta cuando
  // paga en colones.
  const retencion = (() => {
    if (!aplicarRetencion) return { usd: 0, crc: 0 };
    const monto = parseFloat(formData.montoTotal) || 0;
    if (monto <= 0) return { usd: 0, crc: 0 };
    const ordenadas = [...facturas].sort((a, b) => new Date(a.fechaEmision) - new Date(b.fechaEmision));
    let restante = monto;
    let usd = 0, crc = 0;
    for (const f of ordenadas) {
      if (restante <= 0) break;
      const saldo = parseFloat(f.saldoPendiente) || 0;
      const aplicar = Math.min(restante, saldo);
      const subtotal = parseFloat(f.totalVentaNeta || f.subtotal) || 0;
      const total = parseFloat(f.totalComprobante) || 0;
      const baseProp = total > 0 ? subtotal * (aplicar / total) : subtotal;
      const retFac = Math.round(baseProp * 0.02 * 100) / 100;
      usd += retFac;
      const tcFactura = (f.totalColones > 0 && f.totalComprobante > 0)
        ? (parseFloat(f.totalColones) / parseFloat(f.totalComprobante))
        : (parseFloat(f.tipoCambio) || 1);
      crc += retFac * tcFactura;
      restante -= aplicar;
    }
    return { usd, crc: Math.round(crc * 100) / 100 };
  })();
  const retencionTotal = retencion.usd; // compat con handleSubmit y validaciones

  const handleSubmit = async (e) => {
    e.preventDefault();

    const monto = parseFloat(formData.montoTotal);
    if (isNaN(monto) || monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }

    if (monto > totalPendiente + 0.01) {
      toast.error('El monto excede el saldo pendiente total');
      return;
    }

    const esCobroEnColones = cobrarEnColones && moneda === 'USD';
    if (esCobroEnColones && (!montoCRC || parseFloat(montoCRC) <= 0)) {
      toast.error('Ingrese el monto real recibido en colones');
      return;
    }

    try {
      setLoading(true);

      const data = new FormData();
      data.append('facturaIds', JSON.stringify(facturas.map(f => f.id)));
      data.append('fechaPago', formData.fechaPago);
      data.append('montoTotal', monto.toString());
      data.append('moneda', moneda);
      data.append('medioPago', formData.medioPago);
      data.append('numeroComprobante', formData.numeroComprobante || '');
      data.append('banco', formData.banco || '');
      data.append('notas', formData.notas || '');
      if (aplicarRetencion) data.append('aplicarRetencion', 'true');
      if (esCobroEnColones) {
        data.append('pagarEnColones', 'true');
        data.append('montoCRC', montoCRC);
      }
      if (file) data.append('comprobante', file);
      if (excedenteSF) Object.entries(excedenteSF).forEach(([k, v]) => data.append(k, v));

      const resp = await cxcApi.aplicarPagoMultiple(data);
      toast.success(`Pago aplicado a ${facturas.length} facturas`);
      const sf = resp?.data?.data?.saldoFavor;
      if (sf?.error) toast.error(`Cobro aplicado, pero no se registró el saldo a favor: ${sf.error}`, { duration: 8000 });
      else if (sf) toast.success(`Saldo a favor registrado: ${formatMoney(sf.monto, sf.moneda)}`);
      onSuccess();
    } catch (error) {
      console.error('Error aplicando pago múltiple:', error);
      toast.error(error.response?.data?.error || 'Error al aplicar pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-3xl mx-4 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
          <h3 className="text-lg font-semibold text-white">Pago Múltiple — {facturas.length} Facturas</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Cliente Info */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-gray-900">{cliente?.nombre}</p>
              <p className="text-sm text-gray-500">{cliente?.identificacion}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total a Cobrar</p>
              <p className="text-2xl font-bold text-blue-700 ">
                {formatMoney(totalPendiente, moneda)}
              </p>
            </div>
          </div>
        </div>

        {/* Facturas List */}
        <div className="px-6 py-3 border-b max-h-48 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-500 mb-2">Facturas Seleccionadas</p>
          <div className="space-y-1">
            {facturas.map(f => (
              <div key={f.id} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm">
                <span className="font-mono text-gray-700">{f.consecutivo}</span>
                <span className="font-semibold  text-gray-900">
                  {formatMoney(f.saldoPendiente, f.moneda)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago *
              </label>
              <input
                type="date"
                value={formData.fechaPago}
                onChange={(e) => setFormData({ ...formData, fechaPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto Total *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.montoTotal}
                onChange={(e) => setFormData({ ...formData, montoTotal: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 "
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medio de Pago
              </label>
              <select
                value={formData.medioPago}
                onChange={(e) => setFormData({ ...formData, medioPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="sinpe">SINPE Móvil</option>
                <option value="otro">Otro</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                N° Comprobante / Referencia
              </label>
              <input
                type="text"
                value={formData.numeroComprobante}
                onChange={(e) => setFormData({ ...formData, numeroComprobante: e.target.value })}
                placeholder="Ej: 123456"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Banco
              </label>
              <select
                value={formData.banco}
                onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Seleccionar --</option>
                {bancos.map(b => (
                  <option key={b.id} value={b.nombre}>{b.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comprobante (opcional)
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full border rounded-lg px-2 py-1.5 text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
          </div>

          {/* Retención 2% renta (cliente entidad del Estado).
              Base = subtotal sin IVA, prorrateado al monto aplicado por factura (FIFO). */}
          <div className="mt-4 p-3 border border-purple-200 rounded-lg bg-purple-50">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={aplicarRetencion}
                onChange={(e) => setAplicarRetencion(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-purple-800">Retención 2% renta (entidad del Estado)</span>
            </label>
            {aplicarRetencion && (() => {
              const fmt = (n) => (n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              const esCobroCRC = cobrarEnColones && moneda === 'USD';
              const simb = esCobroCRC ? '₡' : (moneda === 'USD' ? '$' : '₡');
              const monto = parseFloat(formData.montoTotal) || 0;
              // Cuando cobrás en colones, la deuda ₡ se recalcula por factura con su T/C.
              // Usa el mismo cálculo del bloque cobrarEnColones para consistencia.
              let montoDisp = monto;
              let retDisp = retencion.usd;
              if (esCobroCRC) {
                const ordenadas = [...facturas].sort((a, b) => new Date(a.fechaEmision) - new Date(b.fechaEmision));
                let restante = monto;
                let deudaCRC = 0;
                for (const f of ordenadas) {
                  if (restante <= 0) break;
                  const saldo = parseFloat(f.saldoPendiente) || 0;
                  const aplicar = Math.min(restante, saldo);
                  const tcFactura = (f.totalColones > 0 && f.totalComprobante > 0)
                    ? (parseFloat(f.totalColones) / parseFloat(f.totalComprobante))
                    : (parseFloat(f.tipoCambio) || 1);
                  deudaCRC += aplicar * tcFactura;
                  restante -= aplicar;
                }
                montoDisp = deudaCRC;
                retDisp = retencion.crc;
              }
              const netoDisp = montoDisp - retDisp;
              return (
                <div className="mt-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monto total a cobrar{esCobroCRC ? ' (₡)' : ''}:</span>
                    <span className="font-mono">{simb}{fmt(montoDisp)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Retención 2% (prorrateada por factura):</span>
                    <span className="font-mono font-medium text-purple-700">−{simb}{fmt(retDisp)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="font-medium text-gray-700">Neto a recibir:</span>
                    <span className="font-mono font-bold text-gray-900">{simb}{fmt(netoDisp)}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Las facturas se saldan al 100%; la retención es crédito fiscal de renta.
                    {esCobroCRC && ' La retención se convierte a ₡ usando el T/C de emisión de cada FE.'}
                  </p>
                </div>
              );
            })()}
          </div>

          {/* Saldo a favor: el depósito trajo de más (en la moneda en que se recibió) */}
          <BloqueExcedente
            moneda={cobrarEnColones && moneda === 'USD' ? 'CRC' : moneda}
            cobrado={cobrarEnColones && moneda === 'USD' ? (parseFloat(montoCRC) || 0) : (parseFloat(formData.montoTotal) || 0)}
            onChange={setExcedenteSF}
          />
          {/* Cobrar en Colones — solo cuando las facturas son USD del mismo cliente.
              Backend prorratea deuda ₡ por factura usando el T/C guardado de cada una y
              guarda diferencial_cambiario a nivel de cobros_cxc (parent). */}
          {moneda === 'USD' && (
            <div className="mt-4 p-3 border border-amber-200 rounded-lg bg-amber-50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cobrarEnColones}
                  onChange={(e) => { setCobrarEnColones(e.target.checked); if (!e.target.checked) setMontoCRC(''); }}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-sm font-medium text-amber-800">Cobrar en Colones (₡)</span>
              </label>
              {cobrarEnColones && (() => {
                // Deuda ₡ total prorrateada FIFO igual que el backend:
                // por cada factura toma su T/C efectivo (totalColones/totalComprobante)
                // y suma neto aplicado (menos retención proporcional) × tcFactura.
                const monto = parseFloat(formData.montoTotal) || 0;
                const ordenadas = [...facturas].sort((a, b) => new Date(a.fechaEmision) - new Date(b.fechaEmision));
                let restante = monto;
                let deudaCRCTotal = 0;
                let tcPromedioNum = 0;
                let tcPromedioDen = 0;
                for (const f of ordenadas) {
                  if (restante <= 0) break;
                  const saldo = parseFloat(f.saldoPendiente) || 0;
                  const aplicar = Math.min(restante, saldo);
                  let retFac = 0;
                  if (aplicarRetencion) {
                    const sub = parseFloat(f.totalVentaNeta || f.subtotal) || 0;
                    const tot = parseFloat(f.totalComprobante) || 0;
                    const base = tot > 0 ? sub * (aplicar / tot) : sub;
                    retFac = Math.round(base * 0.02 * 100) / 100;
                  }
                  const netoAplicado = aplicar - retFac;
                  const tcFactura = (f.totalColones > 0 && f.totalComprobante > 0)
                    ? (parseFloat(f.totalColones) / parseFloat(f.totalComprobante))
                    : (parseFloat(f.tipoCambio) || 1);
                  deudaCRCTotal += netoAplicado * tcFactura;
                  tcPromedioNum += tcFactura * netoAplicado;
                  tcPromedioDen += netoAplicado;
                  restante -= aplicar;
                }
                const tcPromedio = tcPromedioDen > 0 ? tcPromedioNum / tcPromedioDen : 0;
                const cobradoCRC = parseFloat(montoCRC) || 0;
                const diferencial = cobradoCRC - deudaCRCTotal;
                const tipoDif = diferencial >= 0 ? 'Ganancia' : 'Pérdida';
                return (
                  <div className="mt-3 space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Total facturas en ₡ (T/C emisión)</label>
                        <div className="px-3 py-2 bg-white border rounded-lg text-sm font-mono text-gray-700">
                          ₡{deudaCRCTotal.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">T/C promedio ponderado: ₡{tcPromedio.toFixed(2)}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Monto recibido en ₡ *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={montoCRC}
                          onChange={(e) => setMontoCRC(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Monto real en colones"
                          required
                        />
                      </div>
                    </div>
                    {cobradoCRC > 0 && (
                      <div className={`text-xs px-3 py-2 rounded font-mono ${diferencial >= 0 ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                        Diferencial cambiario: <strong>{tipoDif} ₡{Math.abs(diferencial).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-500">
                      La deuda ₡ se prorratea por factura usando su T/C de emisión. Backend guarda el diferencial a nivel del cobro parent.
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></span>
                  Procesando...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Aplicar Cobro
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// EnviarEstadoCuentaModal — envía el estado de cuenta del cliente por email
// ══════════════════════════════════════════════════════════════════════
function EnviarEstadoCuentaModal({ cliente, emisor, saldoFavor = null, onClose }) {
  const emisorNombre = emisor?.nombre || emisor?.nombreComercial || 'Finance 360';
  const [to, setTo] = useState(cliente.email || '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  // Idioma del correo (toggle ES/EN, persistido por receptor)
  const idiomaKey = `f360_estado_cuenta_idioma_${cliente.id}`;
  const [idioma, setIdiomaState] = useState(() => localStorage.getItem(idiomaKey) || 'es');

  // Plantillas y subjects en ambos idiomas. Variables {cliente} y {empresa} se sustituyen.
  const PLANTILLAS = {
    es: {
      subject: `Estado de Cuenta - ${emisorNombre}`,
      mensaje: `Estimados señores {cliente},\n\nReciban un cordial saludo. Adjunto le compartimos el estado de cuenta con el detalle de sus facturas pendientes a la fecha.\n\nAgradecemos su pronta gestión de pago. Quedamos atentos a cualquier consulta.\n\nSaludos cordiales,\n{empresa}`,
      labels: {
        titulo: 'ESTADO DE CUENTA', cliente: 'Cliente', identificacion: 'Identificación', fechaCorte: 'Fecha de corte',
        factura: 'Factura', emision: 'Emisión', vencimiento: 'Vencimiento', estado: 'Estado',
        total: 'Total', cobrado: 'Cobrado', saldo: 'Saldo',
        vencido: 'Venció', cobroParcial: 'Cobro Parcial', alDia: 'Al día',
        saldoVencidoCRC: 'Saldo vencido ₡:', saldoTotalCRC: 'Saldo total ₡:',
        saldoVencidoUSD: 'Saldo vencido $:', saldoTotalUSD: 'Saldo total $:',
        footer: 'Este estado de cuenta fue generado por'
      }
    },
    en: {
      subject: `Account Statement - ${emisorNombre}`,
      mensaje: `Dear {cliente},\n\nWarm greetings. Please find attached your account statement with the detail of all pending invoices to date.\n\nWe appreciate your prompt payment processing. Please don't hesitate to reach out with any questions.\n\nBest regards,\n{empresa}`,
      labels: {
        titulo: 'ACCOUNT STATEMENT', cliente: 'Client', identificacion: 'Tax ID', fechaCorte: 'Statement date',
        factura: 'Invoice', emision: 'Issue Date', vencimiento: 'Due Date', estado: 'Status',
        total: 'Total', cobrado: 'Paid', saldo: 'Balance',
        vencido: 'Overdue', cobroParcial: 'Partial Payment', alDia: 'Current',
        saldoVencidoCRC: 'Overdue balance ₡:', saldoTotalCRC: 'Total balance ₡:',
        saldoVencidoUSD: 'Overdue balance $:', saldoTotalUSD: 'Total balance $:',
        footer: 'This account statement was generated by'
      }
    }
  };

  const [subject, setSubject] = useState(PLANTILLAS[idioma].subject);
  const [mensaje, setMensaje] = useState(PLANTILLAS[idioma].mensaje);

  // Al cambiar idioma: subject siempre regenerado; mensaje carga la plantilla guardada
  // del emisor para ese idioma (si existe), sino la default. Persiste preferencia.
  const setIdioma = async (nuevo) => {
    setIdiomaState(nuevo);
    localStorage.setItem(idiomaKey, nuevo);
    setSubject(PLANTILLAS[nuevo].subject);
    // Cargar plantilla guardada del emisor para ese idioma (si existe)
    try {
      const res = await cxcApi.getEstadoCuentaConfig();
      const d = res.data?.data;
      const tplGuardada = nuevo === 'en'
        ? (d?.mensaje_plantilla_en || d?.mensajePlantillaEn)
        : (d?.mensaje_plantilla_es || d?.mensajePlantillaEs || d?.mensaje_plantilla || d?.mensajePlantilla);
      setMensaje(tplGuardada || PLANTILLAS[nuevo].mensaje);
    } catch {
      setMensaje(PLANTILLAS[nuevo].mensaje);
    }
  };
  const [savingTpl, setSavingTpl] = useState(false);
  const [sending, setSending] = useState(false);
  const [alta, setAlta] = useState(false);

  // Remitente configurable (reutiliza la config de Cotizaciones = correo transaccional del emisor)
  const [configFrom, setConfigFrom] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [cfgEmail, setCfgEmail] = useState('');
  const [cfgName, setCfgName] = useState('');
  const [cfgSaving, setCfgSaving] = useState(false);
  const [firma, setFirma] = useState('');      // firma guardada (data URI), para vista previa
  const [cfgFirma, setCfgFirma] = useState(''); // firma en edición (gear)

  useEffect(() => {
    (async () => {
      try {
        const res = await cxcApi.getEstadoCuentaConfig();
        const d = res.data?.data;
        if (d) {
          setConfigFrom(d.from_email || d.fromEmail || '');
          setCfgEmail(d.from_email || d.fromEmail || '');
          setCfgName(d.from_name || d.fromName || '');
          setFirma(d.firma_imagen || d.firmaImagen || '');
          setCfgFirma(d.firma_imagen || d.firmaImagen || '');
          // Plantillas guardadas por idioma. mensaje_plantilla (legacy) = ES.
          // mensaje_plantilla_en para inglés.
          const tplEs = d.mensaje_plantilla_es || d.mensajePlantillaEs || d.mensaje_plantilla || d.mensajePlantilla;
          const tplEn = d.mensaje_plantilla_en || d.mensajePlantillaEn;
          const tplActiva = idioma === 'en' ? tplEn : tplEs;
          if (tplActiva) setMensaje(tplActiva);
        }
      } catch (e) { /* sin config aún */ }

      // Destinatarios guardados por receptor — priorizan sobre el email de la FE
      try {
        if (cliente?.id) {
          const r = await cxcApi.getReceptorNotifDestinatarios(cliente.id);
          const d = r.data?.data || {};
          const saved = d.recipientEmails;
          if (saved && saved.trim()) {
            setTo(saved);
          } else if (d.receptorEmail && d.receptorEmail.trim()) {
            // Correo de contacto del cliente (ahora admite varios, coma/;)
            setTo(d.receptorEmail);
          } else if (d.emailFacturacion && d.emailFacturacion.trim()) {
            setTo(d.emailFacturacion);
          }
          if (d.ccEmails && d.ccEmails.trim()) {
            setCc(d.ccEmails);
            setShowCc(true);
          }
          if (d.bccEmails && d.bccEmails.trim()) {
            setBcc(d.bccEmails);
            setShowBcc(true);
          }
        }
      } catch (e) { /* sin destinatarios guardados */ }
    })();
  }, [cliente?.id]);

  const onFirmaUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error('La firma no debe superar 1 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setCfgFirma(reader.result);
    reader.readAsDataURL(file);
  };

  const guardarConfig = async () => {
    if (!cfgEmail.trim()) { toast.error('Ingrese el email de envío'); return; }
    setCfgSaving(true);
    try {
      await cxcApi.saveEstadoCuentaConfig({ fromEmail: cfgEmail.trim(), fromName: cfgName.trim(), firmaImagen: cfgFirma || '' });
      setConfigFrom(cfgEmail.trim());
      setFirma(cfgFirma || '');
      setShowConfig(false);
      toast.success('Configuración actualizada');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar la configuración');
    } finally {
      setCfgSaving(false);
    }
  };

  // Guarda el mensaje actual como plantilla predeterminada del emisor — por idioma.
  // ES se guarda también en mensaje_plantilla (legacy compat).
  const guardarPlantilla = async () => {
    setSavingTpl(true);
    try {
      const payload = idioma === 'en'
        ? { mensajePlantillaEn: mensaje }
        : { mensajePlantillaEs: mensaje, mensajePlantilla: mensaje };
      await cxcApi.saveEstadoCuentaConfig(payload);
      toast.success(`Plantilla ${idioma === 'en' ? 'inglés' : 'español'} guardada`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar la plantilla');
    } finally {
      setSavingTpl(false);
    }
  };

  const facturas = (cliente.facturas || []).filter(f => (parseFloat(f.saldoPendiente) || 0) > 0);

  // Sustituye las variables de la plantilla por los datos reales
  const aplicarVars = (txt) => (txt || '')
    .replace(/\{cliente\}/g, cliente.nombre || '')
    .replace(/\{empresa\}/g, emisorNombre);

  // Datos del correo: el servidor lo arma con el diseño Finance360 (vista previa = envío)
  const datosCorreo = () => ({
    idioma,
    mensaje: aplicarVars(mensaje),
    cliente: { nombre: cliente.nombre || '', identificacion: cliente.identificacion || '' },
    saldoFavor: saldoFavor || {},
    facturas: facturas.map(f => ({
      numero: f.numeroConsecutivo || f.consecutivo || '',
      fechaEmision: f.fechaEmision || null,
      fechaVencimiento: f.fechaVencimiento || null,
      total: parseFloat(f.totalComprobante) || 0,
      saldo: parseFloat(f.saldoPendiente) || 0,
      moneda: f.moneda === 'USD' ? 'USD' : 'CRC'
    }))
  });
  const [previewHtml, setPreviewHtml] = useState('');
  const datosClave = JSON.stringify(datosCorreo());
  useEffect(() => {
    let cancelado = false;
    const t = setTimeout(async () => {
      try {
        const r = await cxcApi.previewEstadoCuenta({ datos: JSON.parse(datosClave) });
        if (!cancelado) setPreviewHtml(r.data?.html || '');
      } catch {
        if (!cancelado) setPreviewHtml('<p style="font-family:sans-serif;color:#94a3b8;padding:24px;">No se pudo cargar la vista previa.</p>');
      }
    }, 400);
    return () => { cancelado = true; clearTimeout(t); };
  }, [datosClave, firma]);

  const handleSend = async () => {
    if (!to.trim()) { toast.error('Ingrese un destinatario'); return; }
    if (facturas.length === 0) { toast.error('Este cliente no tiene facturas pendientes'); return; }
    setSending(true);
    try {
      const res = await cxcApi.enviarEstadoCuenta({ receptorId: cliente.id, to, cc, bcc, subject, datos: datosCorreo(), alta });
      toast.success(`Estado de cuenta enviado${res.data?.from ? ` desde ${res.data.from}` : ''}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al enviar el estado de cuenta');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1700px] mx-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b-[3px] border-b-violet-500 rounded-t-xl shrink-0">
          <div className="flex items-center gap-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-3 text-lg">
              <span className="w-9 h-9 rounded-xl border border-indigo-300 flex items-center justify-center"><Mail className="h-4 w-4 text-indigo-600" /></span>
              Enviar Estado de Cuenta
            </h3>
            {/* Toggle de idioma ES/EN */}
            <div className="inline-flex rounded-lg p-0.5 gap-0.5 border border-gray-200">
              <button
                onClick={() => setIdioma('es')}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  idioma === 'es' ? 'border border-indigo-400 text-indigo-700' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <span className="text-base leading-none">🇨🇷</span> Español
              </button>
              <button
                onClick={() => setIdioma('en')}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  idioma === 'en' ? 'border border-indigo-400 text-indigo-700' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <span className="text-base leading-none">🇺🇸</span> English
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-gray-100">
          {/* Columna izquierda: destinatario, remitente y mensaje */}
          <div className="pb-2">
          <div className="px-6 pt-5 space-y-3">
            <div className="flex items-start gap-3">
              <label className="text-sm font-medium text-gray-500 w-14 text-right pt-2">Para:</label>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="email@cliente.com, cobros@cliente.com"
                    className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-violet-400" autoFocus />
                  {!showCc && (
                    <button type="button" onClick={() => setShowCc(true)} className="text-[11px] font-medium text-violet-600 hover:text-violet-800 hover:underline px-1">+ CC</button>
                  )}
                  {!showBcc && (
                    <button type="button" onClick={() => setShowBcc(true)} className="text-[11px] font-medium text-violet-600 hover:text-violet-800 hover:underline px-1">+ CCO</button>
                  )}
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5">Múltiples emails separados por coma. Se guardan por cliente al enviar y se autollenan la próxima vez.</p>
              </div>
            </div>
            {showCc && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-500 w-14 text-right">CC:</label>
                <div className="flex-1 flex items-center gap-2">
                  <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copia@ejemplo.com, otra@ejemplo.com"
                    className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-violet-400" />
                  <button type="button" onClick={() => { setCc(''); setShowCc(false); }} className="text-gray-400 hover:text-red-500" title="Quitar CC"><X className="h-4 w-4" /></button>
                </div>
              </div>
            )}
            {showBcc && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-500 w-14 text-right">CCO:</label>
                <div className="flex-1 flex items-center gap-2">
                  <input type="text" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="copia.oculta@ejemplo.com, otra.oculta@ejemplo.com"
                    className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-violet-400" />
                  <button type="button" onClick={() => { setBcc(''); setShowBcc(false); }} className="text-gray-400 hover:text-red-500" title="Quitar CCO"><X className="h-4 w-4" /></button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-500 w-14 text-right">Asunto:</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-violet-400" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-500 w-14 text-right">Desde:</label>
              <div className="flex-1 flex items-center gap-2 py-1">
                <span className="text-sm">{configFrom ? <span className="text-gray-700 font-medium">{configFrom}</span> : <span className="text-amber-600">Sin remitente configurado</span>}</span>
                <button type="button" onClick={() => setShowConfig(v => !v)} className="text-gray-400 hover:text-violet-600" title="Configurar remitente">
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            </div>
            {showConfig && (
              <div className="ml-14 p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2">
                <p className="text-[11px] text-gray-500">Buzón de Office 365 desde el cual se envían los estados de cuenta (autorizado en la App de Azure). Configuración independiente de cotizaciones.</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email de envío *</label>
                  <input type="email" value={cfgEmail} onChange={(e) => setCfgEmail(e.target.value)} placeholder="facturas@empresa.com" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nombre remitente</label>
                  <input type="text" value={cfgName} onChange={(e) => setCfgName(e.target.value)} placeholder="Cobros - Mi Empresa" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Firma (imagen)</label>
                  {cfgFirma ? (
                    <div className="flex items-center gap-3 mb-1">
                      <img src={cfgFirma} alt="Firma" className="max-h-20 border rounded bg-white p-1" />
                      <button type="button" onClick={() => setCfgFirma('')} className="text-xs text-red-600 hover:underline">Quitar</button>
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-400 mb-1">Subí una imagen (PNG/JPG, máx 1 MB) con tu firma y logo. Se adjunta al pie del correo.</p>
                  )}
                  <input type="file" accept="image/png,image/jpeg" onChange={onFirmaUpload} className="w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-violet-100 file:text-violet-700 hover:file:bg-violet-200" />
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowConfig(false)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-100">Cancelar</button>
                  <button type="button" onClick={guardarConfig} disabled={cfgSaving} className="px-3 py-1.5 text-xs border-2 border-violet-500 text-violet-600 rounded-lg hover:bg-violet-50 disabled:opacity-50">{cfgSaving ? 'Guardando...' : 'Guardar remitente'}</button>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-500">Mensaje:</label>
              <button type="button" onClick={guardarPlantilla} disabled={savingTpl}
                className="text-xs font-medium text-violet-600 hover:text-violet-800 hover:underline disabled:opacity-50 flex items-center gap-1">
                {savingTpl ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Guardar como predeterminado
              </button>
            </div>
            <textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={6}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 resize-none leading-relaxed" />
            <p className="text-[11px] text-gray-400 mt-1">
              Variables: <code className="bg-gray-100 px-1 rounded">{'{cliente}'}</code> y <code className="bg-gray-100 px-1 rounded">{'{empresa}'}</code> se reemplazan automáticamente. Editá el texto y guardá la plantilla para reutilizarla.
            </p>
            <label className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm ${alta ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <input type="checkbox" checked={alta} onChange={(e) => setAlta(e.target.checked)} className="accent-red-600 w-4 h-4" />
              <AlertCircle className="h-4 w-4" />
              Marcar como Prioridad Alta
            </label>
          </div>
          </div>{/* fin columna izquierda */}

          {/* Columna derecha: vista previa */}
          <div className="px-6 py-4 bg-gray-50/40">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Vista previa ({facturas.length} factura{facturas.length !== 1 ? 's' : ''} pendiente{facturas.length !== 1 ? 's' : ''})</p>
            <div className="border border-gray-200 rounded-lg overflow-x-auto shadow-sm bg-white">
              <iframe title="Vista previa del correo" srcDoc={previewHtml} className="w-full h-[720px] border-0 bg-[#EFF1F7]" />
            </div>
          </div>
        </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0">
          <button onClick={onClose} className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancelar</button>
          <button onClick={handleSend} disabled={sending || facturas.length === 0}
            className="px-6 py-2.5 border-2 border-violet-500 text-violet-600 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-violet-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar Estado de Cuenta
          </button>
        </div>
      </div>
    </div>
  );
}
